import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';

import { Fixture } from '@cds/betting-offer';
import { ApiService } from '@frontend/sports/common/api-utils';
import { CDNCachingConfig } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { RouteTag } from '@frontend/sports/common/core/data-access/route';
import { usePreloaderData } from '@frontend/sports/common/core/utils/preloaded-data';
import { LARGE_LAYOUT_BREAKPOINT, MEDIUM_LAYOUT_BREAKPOINT, SMALL_LAYOUT_BREAKPOINT } from '@frontend/sports/layout/feature';
import { ActivatedRouteService } from '@frontend/sports/routing/core/utils';
import { SchemaMarkupService } from '@frontend/sports/schema-markup/feature';
import { ScoreboardCarouselPayload } from '@frontend/sports/types/components/scoreboard-carousel/models';
import {
    LayoutSize,
    Widget,
    WidgetContext,
    WidgetLayoutResponse,
    WidgetPage,
    WidgetResponse,
    WidgetType,
} from '@frontend/sports/types/components/widget';
import { ComposableWidgetPayload } from '@frontend/sports/types/components/widget/types';
import { UserService } from '@frontend/sports/user/feature';
import { MediaQueryService, WINDOW } from '@frontend/vanilla/core';
import { merge, uniqBy } from 'lodash-es';
import { EMPTY, Observable, combineLatest, defer, map, switchMap, take, tap } from 'rxjs';

import { WidgetContextEnhanceService } from './widget-context-enhance.service';

@Injectable({ providedIn: 'root' })
export class WidgetContextService {
    private api = inject(ApiService);
    private media = inject(MediaQueryService);
    private enhanceService = inject(WidgetContextEnhanceService);
    private schemaMarkupService = inject(SchemaMarkupService);
    private cdnCachingConfig = inject(CDNCachingConfig);
    private userSerivce = inject(UserService);

    private readonly _window = inject(WINDOW);

    private readonly activatedRouteService = inject(ActivatedRouteService);

    private readonly sizes = new Map<string, LayoutSize>([
        [LARGE_LAYOUT_BREAKPOINT, LayoutSize.Large],
        [MEDIUM_LAYOUT_BREAKPOINT, LayoutSize.Medium],
        [SMALL_LAYOUT_BREAKPOINT, LayoutSize.Small],
    ]);

    private readonly pages = new Map<RouteTag, WidgetPage>([
        [RouteTag.Lobby, WidgetPage.HomeLobby],
        [RouteTag.SportLobby, WidgetPage.SportLobby],
        [RouteTag.EsportsLobby, WidgetPage.ESportsLobby],
        [RouteTag.Competition, WidgetPage.CompetitionLobby],
        [RouteTag.EventDetails, WidgetPage.EventDetailsPage],
        [RouteTag.MultiSportsLobby, WidgetPage.MultiSportsLobby],
    ]);

    private readonly widgetsWithFixturePayload = [WidgetType.NextToGo, WidgetType.TabbedGrid, WidgetType.OutrightsGrid];

    // TODO Angular 14 update: Setting type for the context parameter is breaking the tests. Investigation needed.
    @usePreloaderData({
        preloader(this: WidgetContextService) {
            return this._window.MS2JS?.ModularHomePageDataLoader;
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        condition(this: WidgetContextService, context: any) {
            if (context.page === WidgetPage.HomeLobby) {
                return true;
            } else if (context.page === WidgetPage.SportLobby) {
                const preloader = this._window.MS2JS?.ModularHomePageDataLoader;

                return (
                    preloader?.requestContext() &&
                    context.sportId === preloader.requestContext().sportId &&
                    context.layoutSize === preloader.requestContext().layoutSize &&
                    Object.keys(context).filter((k) => context[k] != null).length === 3
                );
            }

            return false;
        },
    })
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    getLayout(context: any, enchanceContext = true): Observable<WidgetLayoutResponse> {
        return enchanceContext
            ? this.enhanceContext(context).pipe(
                  switchMap((enhanced) =>
                      this.api.get<WidgetLayoutResponse>(
                          'widget',
                          this.userSerivce.isAuthenticated
                              ? {
                                    ...enhanced,
                                    isAuth: '1',
                                }
                              : enhanced,
                          { transferCache: true },
                      ),
                  ),
              )
            : this.api.get<WidgetLayoutResponse>(
                  'widget',

                  this.userSerivce.isAuthenticated
                      ? {
                            ...context,
                            isAuth: '1',
                        }
                      : context,
                  { transferCache: true },
              );
    }

    getWidget<T = unknown>(
        context: Partial<WidgetContext>,
        addSchemaMarkup = false,
        forceBatchCall = false,
        snapshot?: ActivatedRouteSnapshot,
    ): Observable<Widget<T>> {
        return defer(() => {
            const params = this.getContext({ snapshot });
            if (!context.shouldIncludePayload || forceBatchCall) {
                return this.getWidgetBatch([context], params).pipe(map((widgets) => widgets?.[0] as Widget<T>));
            }
            if (context.shouldIncludePersonalizedData) {
                return this.getPersonalizedWidgetData<T>(context, params);
            }
            return this.getWidgetData<T>(context, params);
        }).pipe(
            tap((widget) => {
                if (addSchemaMarkup && widget.payload && !(!context.shouldIncludePayload || forceBatchCall)) {
                    this.updateSchemaMarkUpJson(widget);
                }
            }),
        );
    }

    preResolveWidgets(route: ActivatedRouteSnapshot) {
        const params = this.getContext({ snapshot: route });
        return this.getLayout(params).pipe(
            switchMap((layout) => {
                if (!layout.widgets) {
                    return EMPTY;
                }
                return combineLatest(
                    layout.widgets
                        .filter((widget) => this.cdnCachingConfig.widgets.includes(widget.type))
                        .flatMap((widget) => {
                            const cdnContext = { widgetId: widget.id, shouldIncludePayload: true };
                            if ([WidgetType.Marquee, WidgetType.OutrightsMarquee].includes(widget.type)) {
                                return [
                                    this.getWidgetData(cdnContext, params),
                                    this.getPersonalizedWidgetData(
                                        {
                                            ...cdnContext,
                                            shouldIncludePersonalizedData: true,
                                        },
                                        params,
                                    ),
                                ];
                            }
                            return [this.getWidgetData(cdnContext, params)];
                        }),
                );
            }),
            take(1),
        );
    }

    getWidgetBatch(contexts: Partial<WidgetContext>[], params = this.getContext()): Observable<Widget<unknown>[] | undefined> {
        return this.enhanceContext(params).pipe(
            switchMap((enhanced) => {
                const data = contexts.map((context) => merge({}, enhanced, context));

                return this.api.post<WidgetResponse>('widget/batch', data, { params: enhanced, transferCache: true });
            }),
            map((collection) => collection.widgets),
        );
    }

    private updateSchemaMarkUpJson(widget: Widget<unknown>): void {
        const widgetsToConsider =
            widget.type === WidgetType.Composable
                ? (widget.payload as ComposableWidgetPayload)?.items?.flatMap(
                      (slot) => slot?.activeChildren?.filter((w) => this.widgetsWithFixturePayload.includes(w?.type)) ?? [],
                  )
                : [widget];

        const payload = uniqBy(
            widgetsToConsider.flatMap((w) => this.getFixtures(w)),
            (f) => f.id,
        );

        if (payload?.length) {
            this.schemaMarkupService.updateModuleEvents(payload);
        }
    }

    private getFixtures(widget: Widget<unknown>): Fixture[] {
        if (widget.type === WidgetType.ScoreboardCarousel) {
            return (widget.payload as ScoreboardCarouselPayload).carouselEvents.map((ev) => ev.fixture);
        }

        return (widget.payload as { fixtures: Fixture[] }).fixtures;
    }

    private getWidgetData<T = unknown>(context: Partial<WidgetContext>, params: WidgetContext): Observable<Widget<T>> {
        return this.enhanceContext(params).pipe(
            switchMap((enhanced) => {
                const data = merge({}, enhanced, context);

                return this.api.get<WidgetResponse>('widget/widgetdata', data, { transferCache: true });
            }),
            map((collection) => (collection.widgets?.length ? collection.widgets[0] : {}) as Widget<T>),
        );
    }

    private getPersonalizedWidgetData<T = unknown>(context: Partial<WidgetContext>, params: WidgetContext): Observable<Widget<T>> {
        return this.enhanceContext(params).pipe(
            switchMap((enhanced) => {
                const data = merge({}, enhanced, context);

                return this.api.get<WidgetResponse>('widget/personalizedWidgetData', data);
            }),
            map((collection) => (collection.widgets?.length ? collection.widgets[0] : {}) as Widget<T>),
        );
    }

    getContext({ snapshot }: { snapshot?: ActivatedRouteSnapshot } = {}): WidgetContext {
        const currentSnapshot = snapshot || this.activatedRouteService.getActivatedRoute();

        const isTennis = currentSnapshot.params.sport && currentSnapshot.params.sport === SportConstant.Tennis;
        const isFakeRegion = currentSnapshot.params.region === '10001' && currentSnapshot.params.league;

        return {
            layoutSize: this.getSize(),
            page: this.getPage(currentSnapshot.data.tag),
            sportId: currentSnapshot.params.sport ? parseInt(currentSnapshot.params.sport) : undefined,
            regionId: currentSnapshot.params.region && !isTennis && !isFakeRegion ? parseInt(currentSnapshot.params.region) : undefined,
            tournamentId: currentSnapshot.params.region && isTennis ? parseInt(currentSnapshot.params.region) : undefined,
            competitionId: !currentSnapshot.params.isVirtual && currentSnapshot.params.league ? parseInt(currentSnapshot.params.league) : undefined,
            virtualCompetitionId:
                currentSnapshot.params.isVirtual && currentSnapshot.params.league ? parseInt(currentSnapshot.params.league) : undefined,
            virtualCompetitionGroupId:
                currentSnapshot.params.isVirtual && currentSnapshot.params.virtualCompetitionGroup
                    ? parseInt(currentSnapshot.params.virtualCompetitionGroup)
                    : undefined,
            fixtureId: currentSnapshot.params.event ? currentSnapshot.params.event : undefined,
            fixtureStage: currentSnapshot.params.fixtureStage ? currentSnapshot.params.fixtureStage : undefined,
            promoBannerPreview: currentSnapshot.queryParams?.offerId && currentSnapshot.queryParams?.sitecoreId ? true : undefined,
        };
    }

    enhanceContext(context: WidgetContext): Observable<WidgetContext> {
        return this.enhanceService.getEnhancedProperties(context).pipe(
            map((additionalProps) => ({
                ...context,
                ...additionalProps,
            })),
        );
    }

    private getPage(tag: RouteTag): WidgetPage {
        const page = this.pages.get(tag);

        if (page) {
            return page;
        }

        throw new Error('Page not supported');
    }

    private getSize(): LayoutSize {
        for (const [query, size] of this.sizes) {
            if (this.media.isActive(query)) {
                return size;
            }
        }

        throw new Error('Layout configuration not supported');
    }
}
